import React, { useState } from 'react';
import Link from 'next/link';
import { routes } from '@portal/env/routes';
import { useRouter } from 'next/router';
import { Button } from '@common/bw-ui/ui/button';
import { Input } from '@common/bw-ui/ui/input';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from '@common/bw-ui/ui/select';
import {
  EventIcon,
  FilterIcon,
  ListIcon,
  SearchIcon,
} from '@common/bw-ui/icons/index';
import { clientConfig } from 'env/config';
import { linkTo } from 'env/utils/strings';
import debounce from 'lodash/debounce';

const debouncedNavigate = debounce((navigate: () => void) => navigate(), 750);

type Category = { Id: number; Title: string };

interface Props {
  categories: Category[];
}

export const EventsPageHeader = ({ categories }: Props) => {
  const router = useRouter();
  const isEventsPage = router.pathname === routes.events;
  const { gymEventId } = clientConfig();

  const [searchValue, setSearchValue] = useState(router.query.query ?? '');

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchValue(query);
    debouncedNavigate(() => {
      void router.push(linkTo(router, { ...router.query, query, page: 1 }));
    });
  };

  const handleCategorySelect = (categoryId: number | null) => {
    void router.push(linkTo(router, { page: 1, categoryId }));
  };

  const filteredCategories = categories.filter((c) => c.Id !== gymEventId);

  const activeCategoryId = router.query.categoryId
    ? Number(router.query.categoryId)
    : null;

  const categoryLabel = activeCategoryId
    ? (filteredCategories.find((c) => c.Id === activeCategoryId)?.Title ??
      'Categories')
    : 'Categories';

  return (
    <div className="tw:flex tw:flex-col tw:gap-4 tw:mb-8">
      <h3 className="tw:font-medium">Upcoming Events</h3>

      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        {isEventsPage && (
          <>
            <div className="tw:relative tw:w-full tw:md:flex-1">
              <SearchIcon
                className="tw:absolute tw:left-3 tw:top-1/2 tw:-translate-y-1/2 tw:size-4 tw:text-muted tw:pointer-events-none"
                fill="gray"
                stroke="currentColor"
                strokeWidth={2}
              />
              <Input
                type="text"
                placeholder="Search events..."
                className="tw:w-full tw:font-normal tw:pl-9"
                value={searchValue}
                onChange={handleSearchChange}
              />
            </div>
            <Select
              value={activeCategoryId?.toString() ?? 'all'}
              onValueChange={(val) =>
                handleCategorySelect(val === 'all' ? null : Number(val))
              }
            >
              <SelectTrigger asChild>
                <Button variant="outline" className="tw:font-normal">
                  <FilterIcon className="tw:w-5 tw:h-5" fill="black" />
                  {categoryLabel}
                </Button>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {filteredCategories.map((category) => (
                  <SelectItem key={category.Id} value={category.Id.toString()}>
                    {category.Title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
        <div className="tw:ml-auto tw:flex tw:h-10 tw:rounded-md tw:border tw:border-border tw:bg-white">
          <Button
            asChild
            variant={isEventsPage ? 'gray-inverted' : 'gray'}
            className="tw:h-full tw:font-normal tw:rounded-l-sm tw:rounded-r-none"
          >
            <Link href={routes.events}>
              <ListIcon className="tw:w-5 tw:h-5" fill="black" />
              Card
            </Link>
          </Button>
          <div className="tw:w-px tw:bg-border" />
          <Button
            asChild
            variant={!isEventsPage ? 'gray-inverted' : 'gray'}
            className="tw:h-full tw:font-normal tw:rounded-l-none tw:rounded-r-sm"
          >
            <Link href={routes.events_calendar}>
              <EventIcon className="tw:w-5 tw:h-5" fill="black" />
              Calendar
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
};
